<template>
  <app-dialog :value="value" title="Mandant wechseln" :max-width="440" blur nested @input="$emit('input', $event)">
    <!-- Schritt 1: Mandant wählen (durchsuchbare Liste wie im Mobility Hub) -->
    <div v-if="!target" class="tswitch">
      <app-search ref="search" v-model="query" class="tswitch__search" placeholder="Kundennummer oder Name" :debounce="0" />
      <p v-if="state === 'loading'" class="tswitch__state">Mandanten werden geladen …</p>
      <div v-else-if="state === 'error'" class="tswitch__state">
        Die Mandanten konnten nicht geladen werden.
        <app-button kind="tertiary" @click="load">Erneut versuchen</app-button>
      </div>
      <ul v-else class="tswitch__list" aria-label="Mandanten">
        <li v-for="customer in filtered" :key="customer.customer_id">
          <button
            type="button"
            class="tswitch__row"
            :disabled="customer.customer_id === currentId"
            :aria-current="customer.customer_id === currentId ? 'true' : null"
            @click="choose(customer)"
          >
            <span class="tswitch__tile" aria-hidden="true"><app-icon name="briefcase" :size="16" /></span>
            <span class="tswitch__name">{{ customer.name }}</span>
            <span class="tswitch__id">{{ customer.customer_id }}</span>
            <app-icon v-if="customer.customer_id === currentId" name="check" :size="14" class="tswitch__check" />
          </button>
        </li>
        <!-- ohne Suche nur der aktuelle Mandant mit Hinweis, Treffer erst ab zwei Zeichen oder einer Ziffer -->
        <li v-if="hintText" class="tswitch__state">{{ hintText }}</li>
      </ul>
    </div>

    <!-- Schritt 2: beim gewählten Mandanten anmelden (Mandant fest wie auf der Anmeldeseite, nur lesbar) -->
    <form v-else class="tswitch tswitch--login" novalidate @submit.prevent="signIn">
      <div v-if="error" class="tswitch__error" role="alert">{{ error }}</div>
      <div class="app-form-field">
        <label class="app-label" for="tswitch-tenant">Mandant</label>
        <app-text-field id="tswitch-tenant" class="tswitch__fixed" :value="targetLabel" readonly />
      </div>
      <div class="app-form-field">
        <label class="app-label" for="tswitch-user">Benutzername</label>
        <app-text-field id="tswitch-user" v-model="username" autocomplete="username" :disabled="busy" />
      </div>
      <div class="app-form-field">
        <label class="app-label" for="tswitch-password">Passwort</label>
        <app-text-field
          id="tswitch-password"
          ref="password"
          v-model="password"
          type="password"
          autocomplete="current-password"
          :disabled="busy"
        />
      </div>
      <!-- Enter im Formular sendet ab -->
      <button type="submit" class="app-sr-only" tabindex="-1">Anmelden</button>
    </form>

    <template v-slot:actions>
      <template v-if="!target">
        <app-button @click="$emit('input', false)">Abbrechen</app-button>
      </template>
      <template v-else>
        <app-button :disabled="busy" @click="$emit('input', false)">Abbrechen</app-button>
        <app-button kind="primary" :loading="busy" @click="signIn">Anmelden</app-button>
      </template>
    </template>
  </app-dialog>
</template>

<script>
// Mandant wechseln (alle angemeldeten Benutzer, tenantSwitchAllowed; geöffnet über das Benutzermenü, das Mehr-Menü oder den
// Mandantenschalter der Hub-Kopfleiste, Zustand $shell.tenantSwitch; mit $shell.tenantPreselect beginnt er beim Passwort): Mandant aus der öffentlichen Kundenliste wählen (wie auf der Anmeldeseite), dann mit
// demselben Benutzernamen und Passwort beim gewählten Mandanten anmelden. Das Anmeldeformular zeigt den Mandanten als
// festes, nur lesbares Feld (wie die Kundenauswahl der Anmeldeseite), dann Benutzername und Passwort; „Abbrechen“
// schließt den Dialog, der Hintergrund ist weichgezeichnet (AppDialog blur; Kundenwunsch 12.10.2026). Gestaltet wie die
// Karten des Dashboards: grauer Rahmen mit Titel oben und Knöpfen unten, Liste bzw. Formular in der weißen inneren Karte
// (AppDialog nested, Kundenwunsch 12.10.2026). Die Anmeldung entspricht der Anmeldeseite
// (Cognito-Benutzer „Mandanten-ID_Benutzername“, Passwort mit Präfix); erst nach Erfolg wird gewechselt: die App lädt
// neu und stellt die Sitzung des neuen Mandanten her. Schlägt die Anmeldung fehl, bleibt die bisherige Sitzung aktiv.
import { Auth } from 'aws-amplify';
import axios from 'axios';
import { customerQueryReady, searchCustomers } from '@/utils/customerSearch';

export default {
  name: 'AppTenantSwitch',
  props: {
    value: { type: Boolean, default: false }
  },
  data() {
    return {
      state: 'idle',
      customers: [],
      query: '',
      target: null,
      username: '',
      password: '',
      error: null,
      busy: false
    };
  },
  computed: {
    // wie in der Kundenauswahl der Anmeldeseite: „Nummer Name“
    targetLabel() {
      return this.target ? `${this.target.customer_id} ${this.target.name}` : '';
    },
    currentId() {
      return this.$store.state.customerId;
    },
    // Benutzername ohne Mandanten-Präfix („12_mhinkel“ → „mhinkel“)
    currentUsername() {
      const name = this.$store.state.cognitoUsername || '';
      const cut = name.indexOf('_');
      return cut === -1 ? name : name.slice(cut + 1);
    },
    // nicht mehr die ganze Liste (Kundenwunsch 13.10.2026: „auch beim mandantenwechsel bitte mit suchfeld“): ohne Suche
    // nur der aktuelle Mandant, sonst höchstens acht Treffer ab zwei Zeichen oder einer Ziffer (src/utils/customerSearch.js)
    filtered() {
      if (!this.query.trim()) {
        return this.customers.filter(c => Number(c.customer_id) === Number(this.currentId));
      }
      return searchCustomers(this.customers, this.query);
    },
    hintText() {
      if (!this.query.trim()) {
        return 'Kundennummer oder Name eingeben';
      }
      if (!customerQueryReady(this.query)) {
        return 'Mindestens 2 Zeichen eingeben';
      }
      return this.filtered.length ? '' : 'Kein Mandant gefunden';
    }
  },
  watch: {
    value(open) {
      if (open) {
        this.reset();
        // in der Hub-Kopfleiste schon gewählt: gleich beim Passwort beginnen
        const preselect = this.$shell.tenantPreselect;
        this.$shell.tenantPreselect = null;
        if (preselect && preselect.customer_id !== this.currentId) {
          this.choose(preselect);
        } else {
          this.focusSearch();
        }
        if (this.state !== 'ready') {
          this.load();
        }
      } else {
        this.password = '';
        this.$shell.tenantPreselect = null;
      }
    }
  },
  methods: {
    reset() {
      this.query = '';
      this.target = null;
      this.password = '';
      this.error = null;
      this.busy = false;
    },
    async load() {
      this.state = 'loading';
      try {
        const response = await axios.create().get(`${this.$store.state.baseUrl}customerpublic`);
        this.customers = Array.isArray(response.data) ? response.data : [];
        this.state = 'ready';
      } catch (e) {
        this.state = 'error';
      }
    },
    choose(customer) {
      this.target = customer;
      this.username = this.currentUsername;
      this.password = '';
      this.error = null;
      this.focusPassword();
    },
    // Fokus ins Suchfeld, damit man gleich tippen kann (wie focusPassword nach dem Öffnen des Dialogs)
    focusSearch() {
      this.$nextTick(() => {
        setTimeout(() => {
          const field = this.$refs.search;
          const input = field && field.$el ? field.$el.querySelector('input') : null;
          if (input) {
            input.focus();
          }
        }, 0);
      });
    },
    // Fokus ins Passwort; erst nach dem Öffnen des Dialogs (v-dialog rendert den Inhalt verzögert und setzt den Fokus
    // nach zwei Ticks selbst auf den Dialog, wenn er noch nicht darin steht)
    focusPassword() {
      this.$nextTick(() => {
        setTimeout(() => {
          const field = this.$refs.password;
          const input = field && field.$el ? field.$el.querySelector('input') : null;
          if (input) {
            input.focus();
          }
        }, 0);
      });
    },
    // aktuelle Seite samt Query (mit Basis des Routers); ohne Route die Übersicht
    returnHref() {
      const route = this.$route;
      if (!route || !route.fullPath || route.path === '/') {
        return '/overview';
      }
      return this.$router.resolve(route.fullPath).href;
    },
    async signIn() {
      if (this.busy || !this.target) {
        return;
      }
      if (!this.username || !this.password) {
        this.error = 'Bitte Benutzername und Passwort eingeben.';
        return;
      }
      this.busy = true;
      this.error = null;
      try {
        const user = await Auth.signIn(`${this.target.customer_id}_${this.username}`, `bconn${this.password}`);
        if (user.challengeName === 'SMS_MFA' || user.challengeName === 'SOFTWARE_TOKEN_MFA') {
          this.error = 'Multifaktor Authentifikation Token.';
        } else if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
          this.error = 'Neues Passwort benötigt.';
        } else if (user.challengeName === 'MFA_SETUP') {
          this.error = 'Multifaktor Authentifikation Setup.';
        } else {
          // gewechselt: dieselbe Seite neu laden (Kundenwunsch 13.10.2026: „beim umloggen eines mandanten bitte die selbe
          // seite wieder aufrufen auf der wir auch davor waren“; vorher immer die Übersicht), die Sitzung des neuen
          // Mandanten wird beim Start wiederhergestellt
          localStorage.setItem('login_customer_id', this.target.customer_id);
          window.location.assign(this.returnHref());
          return;
        }
      } catch (err) {
        if (err && err.code === 'UserNotConfirmedException') {
          this.error = 'Benutzer ist nicht bestätigt, bitte kontaktieren Sie den Administrator.';
        } else if (err && err.code === 'PasswordResetRequiredException') {
          this.error = 'Passwort muss zurückgesetzt werden, bitte kontaktieren Sie den Administrator.';
        } else if (err && (err.code === 'NotAuthorizedException' || err.code === 'UserNotFoundException')) {
          this.error = 'Benutzername oder Passwort ist für diesen Mandanten falsch.';
        } else {
          this.error = 'Der Wechsel ist fehlgeschlagen. Bitte prüfen Sie Ihre Eingaben und versuchen Sie es erneut.';
        }
      }
      this.busy = false;
      this.password = '';
      // nach einem Fehler zurück ins (geleerte) Passwort; der Knopf war während der Anmeldung gesperrt und hat den Fokus
      // verloren
      this.focusPassword();
    }
  }
};
</script>

<style scoped>
.tswitch {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.tswitch .tswitch__search {
  flex: none !important;
  width: 100% !important;
}
.tswitch__list {
  max-height: 320px;
  overflow-y: auto;
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid var(--line);
  border-radius: var(--r-ctl);
}
.tswitch__row {
  width: 100%;
  min-height: 40px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  padding: 4px 12px 4px 8px;
  border: 0;
  border-bottom: 1px solid var(--line-2);
  background: transparent;
  color: var(--ink);
  font-family: var(--font);
  font-size: 13px;
  line-height: 18px;
  text-align: left;
  cursor: pointer;
}
.tswitch__list li:last-child .tswitch__row {
  border-bottom: 0;
}
:where(html:not(.dichte-touch)) .tswitch__row:not(:disabled):hover {
  background: var(--band);
}
.tswitch__row:not(:disabled):active {
  background: var(--tint);
}
.tswitch__row:disabled {
  cursor: default;
  font-weight: 600;
}
.tswitch__row:focus {
  outline: none;
}
.tswitch__row:focus-visible {
  box-shadow: inset 0 0 0 2px var(--link);
}
html.dichte-touch .tswitch__row {
  min-height: 48px;
}
.tswitch__tile {
  width: 24px;
  height: 24px;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--r-ctl);
  background: var(--tile);
  color: var(--tile-fg);
}
.tswitch__name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* Mandant fest: nur lesbar, getönte Fläche statt weiß, kein Text-Cursor */
.tswitch__fixed ::v-deep .app-input {
  background: var(--band);
  color: var(--ink);
  cursor: default;
}
/* Dunkelmodus: --band hebt sich dort kaum von der inneren Karte und den Eingabefeldern ab (#1b1f26 auf #1e232a, ΔL* 1,9
   statt 2,4 im Hellen), das Feld sähe änderbar aus. Daher eingelassen wie deaktivierte Felder (--dis-bg #121519,
   ΔL* 6,9); Schrift --ink (14,8:1) und Kante bleiben, anders als bei deaktivierten Feldern. */
@media screen {
  html.farbschema-dunkel .tswitch__fixed ::v-deep .app-input {
    background: var(--dis-bg);
  }
}
.tswitch__id {
  flex: none;
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-3);
}
.tswitch__check {
  flex: none;
  color: var(--link);
}
.tswitch__state {
  margin: 0;
  padding: 12px;
  font-size: 13px;
  line-height: 20px;
  color: var(--ink-3);
}
.tswitch__error {
  padding: 8px 12px;
  border-radius: var(--r-ctl);
  background: var(--red-t);
  color: var(--red);
}
</style>
